import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, Save, Search, Loader2, CheckCircle2, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

const DAYS = [
  "Segunda",
  "Terça",
  "Quarta",
  "Quinta",
  "Sexta",
  "Sábado",
  "Domingo",
];

function isoMonday(d = new Date()) {
  const date = new Date(d);
  const day = date.getDay(); // 0=Sun, 1=Mon
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  return date.toISOString().slice(0, 10);
}

export default function AdminWeeklySchedule() {
  const [startDate, setStartDate] = useState(isoMonday());
  const [slots, setSlots] = useState(Array(7).fill(null)); // list of sermon objects
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [loading, setLoading] = useState(true);

  // Search state
  const [searchFor, setSearchFor] = useState(null); // index of slot being edited
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const loadCurrent = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/weekly-schedule");
      const filled = Array(7).fill(null);
      (data.items || []).forEach((it, i) => {
        filled[i] = it?.slug ? it : null;
      });
      setSlots(filled);
      if (data.start_date) setStartDate(data.start_date);
    } catch (_) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCurrent();
  }, [loadCurrent]);

  // Debounced search
  useEffect(() => {
    if (searchFor === null) return;
    if (!q.trim()) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/sermons", { params: { q: q.trim(), limit: 12 } });
        setResults(data.items || []);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, searchFor]);

  const chooseSermon = (sermon) => {
    if (searchFor === null) return;
    setSlots((prev) => {
      const copy = [...prev];
      copy[searchFor] = sermon;
      return copy;
    });
    setSearchFor(null);
    setQ("");
    setResults([]);
  };

  const clearSlot = (i) =>
    setSlots((prev) => {
      const copy = [...prev];
      copy[i] = null;
      return copy;
    });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        start_date: startDate,
        slots: slots.filter(Boolean).map((s) => s.slug || s.id),
      };
      await api.put("/admin/weekly-schedule", payload);
      setSavedAt(new Date().toLocaleTimeString("pt-BR"));
      loadCurrent();
    } finally {
      setSaving(false);
    }
  };

  const filledCount = useMemo(() => slots.filter(Boolean).length, [slots]);

  return (
    <section
      className="mt-14 bg-white border border-[#0B1E36]/10 p-8"
      data-testid="admin-weekly-schedule"
    >
      <div className="flex items-center gap-3 text-[#C5A059] mb-6">
        <CalendarDays size={16} strokeWidth={1.5} />
        <h2 className="text-[0.7rem] uppercase tracking-[0.28em] font-medium">
          Agenda semanal, Sermão de hoje
        </h2>
      </div>
      <p className="text-sm text-[#0B1E36]/70 leading-relaxed max-w-3xl">
        Escolha até 7 sermões para a semana. O site exibe automaticamente 1 por
        dia no bloco “Sermão de hoje” da home, com a frase de impacto já pronta
        para compartilhar.
      </p>

      <div className="mt-6 flex items-end gap-4 flex-wrap">
        <div>
          <label className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36]/60">
            Início da semana
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            data-testid="schedule-start-date"
            className="mt-2 border border-[#0B1E36]/15 bg-white py-2 px-3 text-sm"
          />
        </div>
        <div className="text-xs text-[#0B1E36]/60">
          {filledCount} / 7 dias preenchidos
        </div>
        {savedAt && (
          <div className="inline-flex items-center gap-2 text-xs text-emerald-700">
            <CheckCircle2 size={13} /> Salvo às {savedAt}
          </div>
        )}
        <button
          onClick={save}
          disabled={saving || loading}
          data-testid="schedule-save"
          className="ml-auto inline-flex items-center gap-2 bg-[#0B1E36] hover:bg-[#0a1b30] text-white px-6 py-3 text-xs uppercase tracking-[0.22em] font-medium transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} strokeWidth={1.5} />}
          Salvar agenda
        </button>
      </div>

      <ul className="mt-8 divide-y divide-[#0B1E36]/8 border-y border-[#0B1E36]/8">
        {DAYS.map((d, i) => {
          const s = slots[i];
          return (
            <li
              key={i}
              data-testid={`schedule-slot-${i}`}
              className="py-4 grid grid-cols-12 gap-4 items-center"
            >
              <div className="col-span-3 md:col-span-2 text-xs uppercase tracking-[0.22em] text-[#0B1E36]/70">
                Dia {i + 1} · {d}
              </div>
              <div className="col-span-6 md:col-span-7">
                {s ? (
                  <div>
                    <div className="text-sm text-[#0B1E36] font-medium truncate">
                      {s.title}
                    </div>
                    <div className="text-xs text-[#0B1E36]/55">
                      {s.category} {s.verse_reference ? `· ${s.verse_reference}` : ""}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-[#0B1E36]/40 italic">
                    (vazio, clique em Escolher para selecionar um sermão)
                  </div>
                )}
              </div>
              <div className="col-span-3 md:col-span-3 flex justify-end gap-2">
                {s && (
                  <button
                    onClick={() => clearSlot(i)}
                    data-testid={`schedule-clear-${i}`}
                    className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-[#0B1E36]/50 hover:text-rose-600"
                  >
                    <Trash2 size={11} strokeWidth={1.5} /> Limpar
                  </button>
                )}
                <button
                  onClick={() => {
                    setSearchFor(i);
                    setQ("");
                    setResults([]);
                  }}
                  data-testid={`schedule-pick-${i}`}
                  className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-[#C5A059] hover:text-[#B58F4A]"
                >
                  <Search size={11} strokeWidth={1.5} />
                  Escolher
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {searchFor !== null && (
        <div
          data-testid="schedule-search-modal"
          className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center px-6"
          onClick={() => setSearchFor(null)}
        >
          <div
            className="bg-white w-full max-w-2xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-[#0B1E36]/10 flex items-center gap-3">
              <Search size={16} strokeWidth={1.5} className="text-[#0B1E36]/50" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="schedule-search-input"
                placeholder={`Buscar sermão para dia ${searchFor + 1} · ${DAYS[searchFor]}…`}
                className="flex-1 bg-transparent outline-none text-sm"
              />
              {searching && (
                <Loader2 size={14} className="animate-spin text-[#0B1E36]/40" />
              )}
              <button
                onClick={() => setSearchFor(null)}
                className="text-xs uppercase tracking-[0.2em] text-[#0B1E36]/60"
              >
                Fechar
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {results.length === 0 ? (
                <div className="p-8 text-center text-sm text-[#0B1E36]/50">
                  {q ? "Nenhum sermão encontrado." : "Digite para buscar."}
                </div>
              ) : (
                <ul className="divide-y divide-[#0B1E36]/8">
                  {results.map((r) => (
                    <li key={r.id}>
                      <button
                        onClick={() => chooseSermon(r)}
                        data-testid={`schedule-search-result-${r.slug}`}
                        className="w-full text-left px-5 py-3 hover:bg-[#FAF8F4]"
                      >
                        <div className="text-sm text-[#0B1E36] font-medium">
                          {r.title}
                        </div>
                        <div className="text-xs text-[#0B1E36]/55">
                          {r.category}{" "}
                          {r.verse_reference ? `· ${r.verse_reference}` : ""}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
